import { useState } from "react";
import { Key, Copy, Check, AlertCircle, Calendar } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ApiTokenList } from "./ApiTokenList";

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export function ApiToken() {
  const [token, setToken] = useState<string | null>(null);
  const [tokenName, setTokenName] = useState<string>(
    `API Token (${new Date().toLocaleDateString()})`
  );
  const [expiryDate, setExpiryDate] = useState<Date | null>(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // Default: 30 days from now
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [tokenGenerated, setTokenGenerated] = useState(false);

  // Function to generate a new API token
  const generateToken = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if API_BASE_URL is configured
      if (!API_BASE_URL) {
        throw new Error("API base URL is not configured. Please contact support.");
      }

      // Get the current user's session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();


      if (sessionError) {
        console.error("Session Error:", sessionError);
        throw new Error("Failed to get session information");
      }

      if (!session) {
        throw new Error("You must be logged in to generate an API token");
      }

      // Validate token name
      if (!tokenName || tokenName.trim() === "") {
        throw new Error("Please enter a name for the token");
      }

      // Validate expiry date
      if (!expiryDate) {
        throw new Error("Please select an expiry date for the token");
      }

      if (expiryDate < new Date()) {
        throw new Error("Expiry date cannot be in the past");
      }

      // Generate a token using the backend API
      const requestBody = {
        name: tokenName,
        expiresAt: expiryDate.toISOString(),
        description: "Generated from dashboard",
      };


      const response = await fetch(`${API_BASE_URL}/v1/api-tokens`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to generate token: ${response.statusText}`
        );
      }

      const data = await response.json();

      if (!data || !data.token) {
        throw new Error("No token was returned from the server");
      }

      // Set the token and mark as generated
      setToken(data.token);
      setTokenGenerated(true);
    } catch (err) {
      console.error("Error generating token:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  // Function to copy token to clipboard
  const copyToClipboard = () => {
    if (token) {
      navigator.clipboard
        .writeText(token)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy token:", err);
          setError("Failed to copy token to clipboard");
        });
    }
  };

  // Function to reset the token generation process
  const resetToken = () => {
    setToken(null);
    setTokenGenerated(false);
    setError(null);
    setTokenName(`API Token (${new Date().toLocaleDateString()})`);
  };

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center space-x-2">
          <Key className="h-6 w-6 text-indigo-500" />
          <h2 className="text-xl font-bold text-gray-700">API Token</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Generate API Token
            </h3>
            <p className="text-gray-600">
              API tokens allow programmatic access to the Omnylink API. They are
              tied to your account and should be kept secure.
            </p>
            <div className="mt-2 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  <strong>Important:</strong> The token will only be displayed
                  once after generation. Make sure to copy it and store it
                  securely as you won't be able to view it again.
                </p>
              </div>
            </div>
          </div>

          {!tokenGenerated ? (
            <div>
              <div className="mb-6">
                <label
                  htmlFor="tokenName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Token Name
                </label>
                <input
                  type="text"
                  id="tokenName"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter a name for your token"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Give your token a descriptive name to help you identify it
                  later.
                </p>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="expiryDate"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Token Expiry Date
                </label>
                <div className="relative">
                  <div className="flex items-center">
                    <div className="relative">
                      <DatePicker
                        selected={expiryDate}
                        onChange={(date) => setExpiryDate(date)}
                        minDate={new Date()}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholderText="Select expiry date"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Choose when this token should expire. For security reasons, we
                  recommend setting a reasonable expiry date.
                </p>
              </div>

              <button
                onClick={generateToken}
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? "Generating..." : "Generate API Token"}
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your API Token
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <div className="relative flex items-stretch flex-grow">
                    <input
                      type="text"
                      value={token || ""}
                      readOnly
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md sm:text-sm border-gray-300 bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="absolute inset-y-0 right-0 px-3 flex items-center bg-gray-50 rounded-r-md border border-l-0 border-gray-300"
                    >
                      {copied ? (
                        <Check className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  This token will expire on {expiryDate?.toLocaleDateString()}.
                  Make sure to copy it now as it won't be displayed again.
                </p>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={copyToClipboard}
                  className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {copied ? "Copied!" : "Copy Token"}
                </button>
                <button
                  onClick={resetToken}
                  className="flex-1 flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Generate New Token
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Using Your API Token
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="text-md font-medium text-gray-800">
                Authentication
              </h4>
              <p className="text-gray-600 text-sm mt-1">
                Include your API token in the Authorization header of your
                requests:
              </p>
              <div className="mt-2 bg-gray-50 p-3 rounded-md">
                <code className="text-sm text-gray-800">
                  Authorization: Bearer YOUR_API_TOKEN
                </code>
              </div>
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-800">
                Token Expiration
              </h4>
              <p className="text-gray-600 text-sm mt-1">
                Your token will expire on the date you selected. After
                expiration, you'll need to generate a new token.
              </p>
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-800">
                Security Best Practices
              </h4>
              <ul className="list-disc list-inside text-gray-600 text-sm mt-1 space-y-1">
                <li>
                  Never share your API token publicly or commit it to version
                  control
                </li>
                <li>
                  Store your token securely in environment variables or a secure
                  vault
                </li>
                <li>
                  Rotate your tokens regularly by generating new ones and
                  retiring old ones
                </li>
                <li>
                  Use the shortest expiration time that works for your use case
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* List of existing API tokens */}
        <ApiTokenList />
      </div>
    </div>
  );
}
